import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Minus, Plus } from 'lucide-react';

type TimelineItem = {
  title: string;
  description: string;
  tone?: 'default' | 'optional' | 'exam';
};

export const CourseTimeline = ({ items }: { items: TimelineItem[] }) => (
  <div className={`relative mt-6 grid gap-0 ${items.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-6'}`}>
    <div aria-hidden="true" className="absolute bottom-0 left-[9px] top-0 w-1 bg-border md:bottom-auto md:left-0 md:right-0 md:top-[11px] md:h-1 md:w-auto" />
    {items.map(({ title, description, tone = 'default' }) => (
      <div key={title} className="relative pb-5 pl-10 text-sm md:pb-0 md:pl-0 md:pr-3 md:pt-8">
        <span
          aria-hidden="true"
          className={`absolute left-0 top-0 h-[22px] w-[22px] rounded-full border-4 ${
            tone === 'exam'
              ? 'border-primary bg-primary'
              : tone === 'optional'
                ? 'border-primary/20 bg-card'
                : 'border-primary bg-card'
          }`}
        />
        <strong className="block font-medium">{title}</strong>
        <span className="text-muted-foreground">{description}</span>
      </div>
    ))}
  </div>
);

type PriceRow = {
  label: string;
  value: ReactNode;
  total?: boolean;
};

type CoursePriceBlockProps = {
  title: string;
  price: ReactNode;
  unit: string;
  description: string;
  rows: PriceRow[];
  ctaHref: string;
  ctaLabel: string;
};

export const CoursePriceBlock = ({ title, price, unit, description, rows, ctaHref, ctaLabel }: CoursePriceBlockProps) => (
  <div className="grid gap-8 rounded-xl bg-foreground p-6 text-background md:grid-cols-[1.2fr_.8fr] md:p-10">
    <div>
      <h2 className="text-3xl font-medium text-background md:text-4xl">{title}</h2>
      <p className="mt-5 font-heading text-5xl font-medium leading-none text-accent md:text-7xl">
        {price}<span className="text-base font-medium text-background md:text-lg"> {unit}</span>
      </p>
      <p className="mt-4 text-background/75">{description}</p>
      <Button asChild className="mt-6"><a href={ctaHref}>{ctaLabel}</a></Button>
    </div>
    <ul aria-label="Term cost breakdown">
      {rows.map(({ label, value, total }) => (
        <li key={label} className={`flex justify-between gap-4 border-b border-background/20 py-3 last:border-0 ${total ? 'text-lg font-medium text-accent' : ''}`}>
          <span>{label}</span><span>{value}</span>
        </li>
      ))}
    </ul>
  </div>
);

type SessionSegment = {
  label: ReactNode;
  className: string;
};

const sessionSegments: SessionSegment[] = [
  { label: '5', className: 'md:basis-[2.78%] bg-muted' },
  { label: <>Reading Comprehension<br />35 min</>, className: 'md:basis-[19.44%] bg-primary/15' },
  { label: <>Communicating Ideas in Writing<br />25 min</>, className: 'md:basis-[13.89%] bg-accent/30' },
  { label: <>Quantitative Reasoning<br />35 min</>, className: 'md:basis-[19.44%] bg-primary/25' },
  { label: <>Abstract Reasoning<br />20 min</>, className: 'md:basis-[11.11%] bg-accent/20' },
  { label: <>Explanations<br />60 min</>, className: 'md:basis-[33.34%] bg-primary text-primary-foreground' },
];

export const PtcSessionBar = () => (
  <>
    <div className="mt-5 flex flex-col overflow-hidden rounded-xl border-[0.5px] border-border md:h-16 md:flex-row" role="img" aria-label="Session timeline: 5 minutes settle, 35 Reading Comprehension, 25 Communicating Ideas in Writing, 35 Quantitative Reasoning, 20 Abstract Reasoning, 60 explanations">
      {sessionSegments.map(({ label, className }, index) => (
        <div key={index} className={`flex shrink-0 items-center justify-between px-3 py-2 text-center text-xs font-medium leading-tight md:justify-center md:px-1 md:py-0 ${className}`}>{label}</div>
      ))}
    </div>
    <div className="mt-2 flex justify-between text-xs text-muted-foreground sm:text-sm"><span>Start</span><span>Practice test: 115 minutes</span><span>3 hours</span></div>
  </>
);

type InfoItem = { title: string; description: string };

export const ThreeInfoColumns = ({ items }: { items: InfoItem[] }) => (
  <div className="mt-10 grid gap-6 md:grid-cols-3">
    {items.map(({ title, description }) => (
      <div key={title} className="border-l-4 border-primary py-1 pl-4">
        <h3 className="font-medium">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
    ))}
  </div>
);

type PaceItem = {
  title: string;
  detail: string;
  stat: string;
  note: string;
  width?: 'full' | 'short' | 'pattern';
};

export const PaceChart = ({ items }: { items: PaceItem[] }) => (
  <div className="mt-8 grid gap-4">
    {items.map(({ title, detail, stat, note, width = 'full' }) => (
      <div key={title} className="grid items-center gap-4 border-b border-border py-4 md:grid-cols-[220px_1fr_140px]">
        <div><h3 className="text-lg font-medium">{title}</h3><p className="text-sm text-muted-foreground">{detail}</p></div>
        <div className={`h-4 overflow-hidden rounded-full border-[0.5px] border-border ${width === 'pattern' ? 'bg-accent/15' : 'bg-cream'}`}>
          {width !== 'pattern' && <span className={`block h-full rounded-full bg-primary ${width === 'short' ? 'w-[57%]' : 'w-full'}`} />}
        </div>
        <p className="text-left font-heading text-xl font-medium md:text-right">{stat}<span className="block text-sm font-normal text-muted-foreground">{note}</span></p>
      </div>
    ))}
  </div>
);

type FaqItem = {
  question: string;
  answer: ReactNode;
};

export const CourseFaqList = ({ items }: { items: FaqItem[] }) => (
  <div className="mt-6">
    {items.map(({ question, answer }) => (
      <details key={question} className="group border-b border-border py-4">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-lg font-medium [&::-webkit-details-marker]:hidden">
          {question}
          <Plus aria-hidden="true" className="h-5 w-5 shrink-0 text-primary group-open:hidden" />
          <Minus aria-hidden="true" className="hidden h-5 w-5 shrink-0 text-primary group-open:block" />
        </summary>
        <div className="mt-2 text-muted-foreground">{answer}</div>
      </details>
    ))}
  </div>
);
